import { LegalPage, MailTo, Section } from "./legal/LegalPage";

/** A simple, self-contained privacy policy page (linked from the site footer). */
export function Privacy() {
  return (
    <LegalPage title="Privacy Policy" updated="November 3, 2026">
      <p>
        goonlords is built by gooners, for gooners — and we take your privacy seriously.
        This page explains, in plain language, what we collect and what we do with it. The
        short version: we keep as little as possible, we never sell it, and we keep it
        discreet.
      </p>

      <Section title="What we collect">
        <ul className="list-disc space-y-1 pl-5">
          <li>
            <span className="text-zinc-100">Account basics</span> — if you create an account:
            a username, a securely hashed password, an email address, your date of birth, and
            your gender. We also keep your answers to three security questions — so you can
            get back in, and, in aggregate and never next to your name, so we know who we
            are building for. No real name is required to use the site.
          </li>
          <li>
            <span className="text-zinc-100">Shipping details, only if you buy from the shop</span>{" "}
            — the name and US address for your parcel. They are encrypted, used only to ship
            your order, and deleted 30 days after it ships or is refunded. You can buy without
            an account. The email you enter at checkout goes to the payment processor for your
            receipt; we keep only a one-way, keyed fingerprint of it, which lets you find your
            order with its number and that email and can&rsquo;t be turned back into the address.
          </li>
          <li>
            <span className="text-zinc-100">Your email, and what we don&rsquo;t do with it</span>{" "}
            — we will never email you. No newsletters, no notifications, no &ldquo;we miss
            you&rdquo;. It exists for one purpose: getting you back into your account if you
            lose your password. We don&rsquo;t sell it, share it, or hand it to an ad network.
          </li>
          <li>
            <span className="text-zinc-100">Your setup</span> — the views, screens, and
            collections you build, so we can sync them across your devices.
          </li>
          <li>
            <span className="text-zinc-100">Usage signals</span> — anonymous activity like
            views, faps, and edges, used only to power your stats and the catalog.
          </li>
          <li>
            <span className="text-zinc-100">Anonymous product analytics</span> — page views,
            taps, and short replays of how the browsing and remote pages are used, so we can
            fix what&rsquo;s confusing. Never tied to an account, never on the screen itself,
            and never what you type or watch: clips are blanked out and inputs are masked.
          </li>
        </ul>
      </Section>

      <Section title="What we don't do">
        <ul className="list-disc space-y-1 pl-5">
          <li>We don&rsquo;t sell or rent your data to anyone. Ever.</li>
          <li>
            We don&rsquo;t email you — not once. The address on your account is for getting
            back in, and nothing else.
          </li>
          <li>
            We don&rsquo;t ask for your phone number or any proof of identity, and we ask for a
            real name only to address a parcel you ordered.
          </li>
        </ul>
      </Section>

      <Section title="Advertising">
        Some pages carry banner ads served by JuicyAds, a third-party ad network. To serve
        and count those ads, JuicyAds receives your IP address and browser details and may
        set its own cookies — this is their processing, under{" "}
        <a
          href="https://www.juicyads.com/privacy"
          rel="noreferrer nofollow"
          target="_blank"
          className="text-[var(--color-accent)] hover:underline"
        >
          their privacy policy
        </a>
        , not ours. We pass them nothing about you: no account, no username, no viewing
        history. Ads never appear on your screens or displays, and never before the age
        gate. An ad blocker removes them with no effect on the rest of the site.
      </Section>

      <Section title="Discretion">
        goonlords is designed to be discreet. Signed-out use works with a local, on-device
        identity — nothing is tied to you personally. With an account, the only personal
        details we hold are the ones on the sign-up form — an email for getting back in, a
        date of birth, and a gender — plus, if you order from the shop, your shipping details
        until they are deleted. None of it appears on your public page and none of it
        goes to an advertiser, and your answers to the security questions are never shown to
        another member, and never on your profile. Shared screen links and private order links are
        unguessable and only work for people you hand them to.
      </Section>

      <Section title="Data you control">
        You can delete your views, collections, and account at any time. When you remove
        something, it&rsquo;s gone from your view immediately and purged from our systems.
      </Section>

      <Section title="Contact">
        Questions about your privacy? Write to <MailTo subject="Privacy request" /> and
        we&rsquo;ll help. Other routes are on the{" "}
        <a href="/contact" className="text-[var(--color-accent)] hover:underline">
          contact page
        </a>
        .
      </Section>
    </LegalPage>
  );
}
